import type { ReactNode } from 'react';
import { Connections, type Outcome, type Scope } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Facts, Icon, type IconName, Section } from '@beyond-js/ui/react';

/**
 * What the decision leaves the app able to do, beside the choice and updated with every change: what it
 * adds, keeps and removes, where and until when, and the decision's actions. A removal says when it
 * reaches the product; what Cancel keeps is said only when the app has something to keep.
 */
export function Summary({ outcome, where, until, product, held, ready, note, actions }: {
	outcome: Outcome;
	where: string;
	until: string;
	product: string;
	/** Whether the app already has something here */
	held: boolean;
	/** Whether Allow has something to give */
	ready: boolean;
	/** A line about the decision itself, such as the sign-in it asks to confirm */
	note?: ReactNode;
	actions: ReactNode;
}) {
	const { text, date } = usePresence();
	const parts: { key: string; title: string; scopes: Scope[]; glyph: IconName }[] = [
		{ key: 'adds', title: text('permissions.adds'), scopes: outcome.adds, glyph: 'plus' },
		{ key: 'keeps', title: text('permissions.keeps'), scopes: outcome.keeps, glyph: 'check' },
		{ key: 'removes', title: text('permissions.removes'), scopes: outcome.removes, glyph: 'minus' }
	];
	return (
		<Section title={text('permissions.summary')} level={2}>
			<div className="flex flex-col gap-4" aria-live="polite">
				{parts
					.filter(part => part.scopes.length)
					.map(part => (
						<Changed key={part.key} title={part.title} scopes={part.scopes} glyph={part.glyph} product={product} />
					))}
				{ready ? null : <p className="text-small text-ink-muted">{text('permissions.nothing')}</p>}
			</div>
			<div className="mt-4 flex flex-col gap-4">
				<Facts
					label={text('permissions.summary')}
					rows={[
						{ key: 'where', label: text('permissions.where'), value: where },
						{ key: 'until', label: text('permissions.until'), value: date(until) }
					]}
				/>
				{outcome.removes.length ? <p className="text-small text-ink-muted">{text('permissions.removal', { product })}</p> : null}
				{note}
				<div className="flex flex-wrap gap-3">{actions}</div>
				<p className="text-small text-ink-muted">
					{held ? `${text('permissions.cancel')} ` : null}
					{text('connect.later')}
				</p>
			</div>
		</Section>
	);
}

/** One part of the outcome: its title and the permissions in it, each with its mark */
function Changed({ title, scopes, glyph, product }: { title: string; scopes: Scope[]; glyph: IconName; product: string }) {
	const { text, language } = usePresence();
	return (
		<div>
			<h3 className="text-small font-semibold text-ink" data-bui-label>
				{title}
			</h3>
			<ul className="mt-1 flex flex-col gap-1">
				{scopes.map(scope => (
					<li key={scope.name} className={`permission-change permission-change-${glyph}`}>
						<Icon name={glyph} size={16} />
						<span>{Connections.title(scope, language) ?? text('permissions.undescribed', { product })}</span>
					</li>
				))}
			</ul>
		</div>
	);
}
